/**
 * DOM helpers for the tour overlay and auto-start holds. Kept apart from the React code so the
 * geometry and the dialog-hold rule are unit testable on their own.
 */
import {
  VIEWPORT_MARGIN,
  computeAnchoredPosition,
  type AnchorRect,
  type PanelPosition,
} from "../../utils/anchoredPosition";
import type { TourStep } from "../../types/tours";
import { canAutoPressAnchor, POPUP_CLOSE_ANCHORS, TOUR_ANCHOR_ATTRIBUTE } from "./tourAnchors";
import { stepInteraction, type TourRun } from "./tourEngine";

/** Marks the overlay root so the hold and inert logic can tell it apart from host dialogs. */
export const TOUR_OVERLAY_ATTRIBUTE = "data-tour-overlay";

/**
 * Marks an element the tour itself made inert, so the tour's own shield can be told apart from a
 * panel that marks itself closed with `inert`.
 *
 * Without it, everything the tour has shielded looks shut: `openHostDialogs` ignores an `inert`
 * subtree, and while a plain step is on screen every child of `body` is inert — including the
 * modal the coach is standing inside. Asked from the back walk, that reads as "no dialog is open"
 * at exactly the moment one is.
 */
export const TOUR_INERT_ATTRIBUTE = "data-tour-inert";

const isRendered = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
};

/**
 * The first visible element tagged with `anchorId`, falling back to the first match at all (jsdom
 * and a still-laying-out page both report zero rects). Null when nothing carries the anchor.
 *
 * An anchor inside an `aria-hidden="true"` subtree does not count, for the same reason
 * `openHostDialogs` ignores one: a slide panel that stays mounted while closed (`ManageTeamsPanel`)
 * still has a full-height box, parked off screen by a transform. Without this it measures as a
 * perfectly good rect and the spotlight lands on empty space at the edge of the viewport, rather
 * than on the centered card a step whose element is not on screen is supposed to get.
 *
 * `inert` is deliberately *not* treated the same way: the overlay inerts every other child of
 * `body` while a tour is up, so every anchor on the page is inside an inert subtree for the whole
 * of the run.
 */
/** Selector for everything tagged with `anchorId`, with the id escaped for an attribute value. */
export const anchorSelector = (anchorId: string): string =>
  `[${TOUR_ANCHOR_ATTRIBUTE}="${anchorId.replace(/["\\]/g, "\\$&")}"]`;

export const findAnchorElement = (
  anchorId: string,
  root: ParentNode = document
): HTMLElement | null => {
  const matches = Array.from(
    root.querySelectorAll<HTMLElement>(anchorSelector(anchorId))
  ).filter(
    (element) =>
      !element.closest(`[${TOUR_OVERLAY_ATTRIBUTE}]`) &&
      !element.closest('[aria-hidden="true"]')
  );
  return matches.find(isRendered) ?? matches[0] ?? null;
};

/**
 * The anchor element only when it actually has a box.
 *
 * `findAnchorElement` deliberately falls back to the first match with no rect, because a page that
 * has not laid out yet still has the right element. Anywhere that is about to *do* something to the
 * element — focus it, or count it as a way forward — needs the stricter test instead: a
 * `display: none` control cannot take focus, so admitting it to the tour's Tab ring would put a
 * dead stop where a keyboard user's only route to Skip is.
 */
export const findRenderedAnchorElement = (
  anchorId: string,
  root: ParentNode = document
): HTMLElement | null => {
  const element = findAnchorElement(anchorId, root);
  return element && isRendered(element) ? element : null;
};

/**
 * The longest control name the bubble will name in its instruction.
 *
 * The point of naming the control is that "Click Columns to continue" is one glance. A row anchor's
 * text content can be a whole table row, and pasting that into the bold line rebuilds the paragraph
 * this replaced — so anything past a short button's worth of words reads as no usable name at all
 * and the bubble falls back to "the highlighted control".
 */
const MAX_CONTROL_NAME_LENGTH = 32;

/**
 * What to call the control a `ClickToContinue` step is asking for, taken from the element itself.
 *
 * `aria-label` first, because an icon-only control (the tags Close button) has no text worth
 * reading, then the trimmed text content. Null when neither gives a short, usable name, and the
 * bubble says "the highlighted control" instead — the sentence has to read either way.
 *
 * Whitespace is collapsed: JSX indents its children, so a button's `textContent` arrives with
 * newlines in it.
 */
export const anchorControlName = (element: HTMLElement | null): string | null => {
  if (!element) return null;
  const candidates = [element.getAttribute("aria-label"), element.textContent];
  for (const candidate of candidates) {
    const name = candidate?.replace(/\s+/g, " ").trim() ?? "";
    if (name && name.length <= MAX_CONTROL_NAME_LENGTH) return name;
  }
  return null;
};

/** What the run already knows about its own steps, and how the overlay is rendering them. */
export interface BackWalkContext {
  /** The overlay is rendering every step as a centered card (a data tour that lost its rows). */
  suppressAnchor: boolean;
  /** Steps this run has already had their press for, by the coach or by the tour itself. */
  satisfiedStepKeys: readonly string[];
}

/**
 * Whether Back should walk past `step` rather than land on it.
 *
 * Three reasons, and they are the same question the overlay answers when it renders a step:
 *
 * - The press it wanted is already spent (this run's own record), whether the coach made it or the
 *   tour made it for them. Those steps are one-way: the press is what opened the thing the tour is
 *   now standing in, and landing back on "Manage teams" would offer, or repeat, a press that
 *   *closes* the panel again — leaving the bubble describing something no longer on screen.
 * - Its control has gone. A card explaining a Close button that is no longer on screen is worse
 *   than the readable step before it.
 * - It is still asking for a click right now. The exception is a control that says it is already
 *   expanded: the overlay renders that as read-and-Next, so landing on it asks for nothing.
 *
 * A step authored without an anchor, and any step while `suppressAnchor` is on, is a centered card
 * the coach can simply read — never walked past, or a data tour with no rows would have no Back at
 * all.
 */
export const shouldWalkPastOnBack = (
  step: TourStep,
  { suppressAnchor, satisfiedStepKeys }: BackWalkContext,
  root: ParentNode = document
): boolean => {
  if (satisfiedStepKeys.includes(step.key)) return true;
  if (suppressAnchor || step.anchor === null) return false;
  const element = findRenderedAnchorElement(step.anchor, root);
  if (!element) return true;
  return (
    stepInteraction(step) === "ClickToContinue" &&
    element.getAttribute("aria-expanded") !== "true"
  );
};

/**
 * Open `aria-modal` dialogs that are not the tour's own bubble.
 *
 * A slide panel that stays mounted while closed (for its transition) keeps `aria-modal="true"` and
 * marks itself closed with `inert` or `aria-hidden`. Counting it as open held the Players page tour
 * forever, so anything inside an inert or hidden subtree is not an open dialog.
 */
export const openHostDialogs = (
  root: ParentNode = document,
  /**
   * Ignore the `inert` the tour itself applied. Auto-start asks with this off, because no tour is
   * on screen to have shielded anything; the back walk asks with it on, because one is.
   */
  { ignoreTourInert = false }: { ignoreTourInert?: boolean } = {}
): HTMLElement[] => {
  const closed = ignoreTourInert
    ? `[aria-hidden="true"], [inert]:not([${TOUR_INERT_ATTRIBUTE}])`
    : '[inert], [aria-hidden="true"]';
  return Array.from(root.querySelectorAll<HTMLElement>('[aria-modal="true"]')).filter(
    (dialog) => !dialog.closest(`[${TOUR_OVERLAY_ATTRIBUTE}]`) && !dialog.closest(closed)
  );
};

/**
 * Whether an open dialog should hold this run back.
 *
 * A tour waits for every dialog to close: spotlighting the page behind a modal points at controls
 * the user cannot reach. The exception is a tour whose first anchored step sits inside that dialog,
 * because then the dialog is what it explains. That is a flow-step tour over its modal, or a page
 * tour for a view that is itself a dialog (the schedule's itinerary catalog). A data tour never
 * qualifies: its rows live on the page behind.
 */
export const isHeldByOpenDialog = (run: TourRun, root: ParentNode = document): boolean => {
  const dialogs = openHostDialogs(root);
  if (dialogs.length === 0) return false;
  if (run.tour.kind === "Data") return true;

  const firstAnchored = run.steps.find((step) => step.anchor !== null);
  const anchor = firstAnchored?.anchor ? findAnchorElement(firstAnchored.anchor, root) : null;
  if (!anchor) return true;
  return !dialogs.every((dialog) => dialog.contains(anchor));
};

/** Where Back lands, and whether the step it lands on asks for its click again. */
export interface BackTarget {
  stepIndex: number;
  /**
   * The landing step is a spent `ClickToContinue` opener (Filter, Manage Tags, Columns). Before
   * moving, the caller closes what the run opened, and the press is no longer counted as spent for
   * this step or any after it, so the coach is back on the page with the control asking for its
   * click, exactly as they first met it.
   */
  rearm: boolean;
}

/**
 * Whether the tour can take the coach back out of `dialog` itself: it is a popup a pressed opener
 * of this run shows, and its close button is one the tour may press (`POPUP_CLOSE_ANCHORS`).
 */
const runCanClose = (
  dialog: HTMLElement,
  steps: readonly TourStep[],
  satisfiedStepKeys: readonly string[],
  root: ParentNode
): boolean =>
  steps.some((step) => {
    if (step.anchor === null || !satisfiedStepKeys.includes(step.key)) return false;
    const closeId = POPUP_CLOSE_ANCHORS.get(step.anchor);
    if (!closeId || !canAutoPressAnchor(step.anchor) || !canAutoPressAnchor(closeId)) return false;
    const close = findRenderedAnchorElement(closeId, root);
    return close !== null && dialog.contains(close);
  });

/**
 * A step Back can land on by undoing its press: a `ClickToContinue` step whose click this run
 * spent, on a control the tour is allowed to press, that is still on the page (under a popup it
 * opened, or with that popup since closed). `OpenAnchor` steps are left out on purpose: the tour
 * presses those itself, so landing on one would open the popup again on arrival.
 */
const isRearmableOpener = (
  step: TourStep,
  satisfiedStepKeys: readonly string[],
  root: ParentNode
): boolean => {
  if (
    step.anchor === null ||
    !satisfiedStepKeys.includes(step.key) ||
    stepInteraction(step) !== "ClickToContinue" ||
    !canAutoPressAnchor(step.anchor)
  ) {
    return false;
  }
  const opener = findRenderedAnchorElement(step.anchor, root);
  if (!opener) return false;
  // Only an opener whose popup the tour knows how to shut: through its close button, or by pressing
  // a toggle that says it is open. Anything else could be left open with its press forgotten.
  const closeId = POPUP_CLOSE_ANCHORS.get(step.anchor);
  return (closeId !== undefined && canAutoPressAnchor(closeId)) || opener.hasAttribute("aria-expanded");
};

/**
 * Where Back should land from `fromIndex`, or null when it should stay put.
 *
 * It walks backwards past every step that cannot be shown as authored right now
 * (`shouldWalkPastOnBack`) and stops at the first one that can simply be read.
 *
 * **Leaving a popup goes back to the step that opened it.** The first spent opener the walk meets
 * (Filter, Manage Tags, Columns) is a landing place, re-armed: the caller closes what the run opened
 * and the step asks for its click again. Before this, Back out of the Filter modal did nothing,
 * Back out of the tag manager did nothing, and Back from Columns walked past the whole filter
 * section to "Add a single player", because every one of those openers counted as spent and so was
 * walked past, while the step before them sat behind the open dialog.
 *
 * Otherwise it will not walk **out of a dialog that is open**. A step behind one is spotlit under a
 * scrim, on a page the next step inerts again, with Escape mapped to Skip: the coach is looking at a
 * highlight around something they cannot see. So the walk stops at the first step of the detour
 * they are inside, and Back does nothing at all when that detour's first step is where they already
 * are. A re-armed opener behind a dialog the tour cannot close itself is held to the same rule.
 *
 * The dialog question is the same one auto-start asks (`openHostDialogs`), asked from the other
 * side, so the two cannot disagree about what counts as open.
 *
 * Index 0 is still a landing place whatever it asks for — somewhere readable beats a Back button
 * that does nothing — as long as it is not behind an open dialog.
 */
export const backTarget = (
  steps: readonly TourStep[],
  fromIndex: number,
  context: BackWalkContext,
  root: ParentNode = document
): BackTarget | null => {
  // While every step is a centered card there is no detour to be inside: cards render above the
  // scrim, and a data tour with no rows would otherwise lose Back entirely.
  const dialogs = context.suppressAnchor ? [] : openHostDialogs(root, { ignoreTourInert: true });
  const isBehindAnOpenDialog = (step: TourStep): boolean => {
    if (dialogs.length === 0 || step.anchor === null) return false;
    const anchor = findAnchorElement(step.anchor, root);
    return anchor !== null && !dialogs.every((dialog) => dialog.contains(anchor));
  };

  for (let index = fromIndex - 1; index >= 0; index -= 1) {
    const step = steps[index];
    if (!context.suppressAnchor && isRearmableOpener(step, context.satisfiedStepKeys, root)) {
      const closable = dialogs.every((dialog) =>
        runCanClose(dialog, steps, context.satisfiedStepKeys, root)
      );
      return closable ? { stepIndex: index, rearm: true } : null;
    }
    if (isBehindAnOpenDialog(step)) return null;
    if (index === 0 || !shouldWalkPastOnBack(step, context, root)) {
      return { stepIndex: index, rearm: false };
    }
  }
  return null;
};

/**
 * How deep we are inside a press the tour made itself, rather than the coach making it.
 *
 * `element.click()` dispatches synchronously, so a counter around the call is enough for any
 * listener that fires during it to ask who pressed. `ClickToContinue` is the listener that has to
 * ask: it matches on the anchor selector alone, so the end-of-run tidy-up pressing a control that
 * a step of the same run is watching is indistinguishable from the coach pressing it — and the
 * result is a second progress record, with the wrong outcome. No anchor in today's manifest is
 * used by two steps of one tour, but steps are authored in the API and an admin can write exactly
 * that.
 *
 * `event.isTrusted` would answer the same question in a browser and nowhere else: jsdom reports
 * `false` for every event a test fires, so every interactive step in the suite would read as the
 * tour pressing its own control.
 */
let tourPressDepth = 0;

/** Whether the click being handled right now is one the tour made on the coach's behalf. */
export const isTourInitiatedPress = (): boolean => tourPressDepth > 0;

/**
 * Presses `element` as the tour rather than as the coach, so the tour's own listeners can tell the
 * two apart. Every programmatic press the tour makes goes through here.
 */
export const pressAnchorElement = (element: HTMLElement): void => {
  tourPressDepth += 1;
  try {
    element.click();
  } finally {
    tourPressDepth -= 1;
  }
};

/**
 * Closes what this run opened, once, as it ends.
 *
 * A tour that ends on a detour leaves the popup it asked the coach to open sitting there — and
 * when the page tour chains into the data tour, that popup is inerted behind the next run's shade
 * with no way to reach it: `ColumnSettingsPanel` has no close control, its outside-press dismissal
 * never fires because the overlay swallows `mousedown`, and Escape on a plain step is the tour's.
 *
 * Bounded three ways, so the tour can never press something it did not open: the step's click has
 * to be one this run recorded (a coach's press on a `ClickToContinue` control, or the tour's own
 * `OpenAnchor` press), the anchor has to be one the tour was allowed to press in the first place
 * (`AUTO_PRESS_ANCHORS`), and the control has to still say it is open. A control with no
 * `aria-expanded` is left alone rather than guessed at — a close button carries no such flag, and
 * pressing one of those is how the tidy-up would start closing things on its own.
 *
 * Returns the anchors it pressed, for the caller's tests to name.
 */
export const closeAnchorsOpenedByRun = (
  steps: readonly TourStep[],
  satisfiedStepKeys: readonly string[],
  root: ParentNode = document
): string[] => {
  // No next step: the run is leaving, so every popup it opened goes.
  const pressed = closePopupsLeftBehind(steps, satisfiedStepKeys, null, root);
  const seen = new Set<string>();
  for (const step of steps) {
    const anchorId = step.anchor;
    if (anchorId === null || seen.has(anchorId)) continue;
    if (!satisfiedStepKeys.includes(step.key) || !canAutoPressAnchor(anchorId)) continue;
    seen.add(anchorId);
    // A popup whose opener publishes no `aria-expanded` is closed through its own close button,
    // by the `closePopupsLeftBehind` call at the top of this function.
    if (POPUP_CLOSE_ANCHORS.has(anchorId)) continue;
    const element = findRenderedAnchorElement(anchorId, root);
    if (!element || element.getAttribute("aria-expanded") !== "true") continue;
    // Marked as the tour's own press: a `ClickToContinue` step still watching this same anchor
    // must not read the tidy-up as the coach working the control.
    pressAnchorElement(element);
    pressed.push(anchorId);
  }
  return pressed;
};

/**
 * Closes a popup this run opened when the tour moves on to a step that is not inside it.
 *
 * The manifest no longer spends a step on "Close it when you are done": the coach presses Next on
 * the last step inside the tag manager and the tour closes it for them, so the step after it
 * points at a page that is not covered by a modal. Only popups listed in `POPUP_CLOSE_ANCHORS`
 * are closed, both sides of the pair must be in `AUTO_PRESS_ANCHORS`, the opener's press must be
 * in this run's record, and the close button must sit inside a `role="dialog"`.
 *
 * "Not inside it" is decided only from what is on the page. When the next step's anchor cannot be
 * found at all, the popup stays open: the anchor may not have rendered yet (the tag manager draws
 * its categories after they load, and none at all once an org has the maximum), and closing then
 * would leave that step pointing at nothing. The end of the run closes it instead, which is the
 * `nextStep = null` call from `closeAnchorsOpenedByRun`. A next step with no anchor at all is a
 * centered card over the page, so the popup goes.
 *
 * Returns the close anchors it pressed, for the caller's tests to name.
 */
export const closePopupsLeftBehind = (
  steps: readonly TourStep[],
  satisfiedStepKeys: readonly string[],
  nextStep: TourStep | null,
  root: ParentNode = document
): string[] => {
  const pressed: string[] = [];
  for (const step of steps) {
    const openerId = step.anchor;
    if (openerId === null || !satisfiedStepKeys.includes(step.key)) continue;
    const closeId = POPUP_CLOSE_ANCHORS.get(openerId);
    if (!closeId || pressed.includes(closeId)) continue;
    if (!canAutoPressAnchor(openerId) || !canAutoPressAnchor(closeId)) continue;
    const close = findRenderedAnchorElement(closeId, root);
    const popup = close?.closest('[role="dialog"]');
    if (!close || !popup) continue;
    if (nextStep?.anchor) {
      const nextAnchor = findAnchorElement(nextStep.anchor, root);
      if (!nextAnchor || popup.contains(nextAnchor)) continue;
    }
    pressAnchorElement(close);
    pressed.push(closeId);
  }
  return pressed;
};

export interface BubblePlacementInput {
  anchor: AnchorRect;
  bubbleWidth: number;
  bubbleHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  gap: number;
}

/**
 * Below or above the anchor when either side fits, otherwise beside it, otherwise the clamped
 * vertical placement `usePopoverPosition` uses. Never off screen.
 */
export const placeTourBubble = ({
  anchor,
  bubbleWidth,
  bubbleHeight,
  viewportWidth,
  viewportHeight,
  gap,
}: BubblePlacementInput): PanelPosition => {
  const vertical = () =>
    computeAnchoredPosition({
      anchor,
      panelWidth: bubbleWidth,
      panelHeight: bubbleHeight,
      viewportWidth,
      viewportHeight,
      gap,
    });

  const fitsBelow = viewportHeight - anchor.bottom - gap - VIEWPORT_MARGIN >= bubbleHeight;
  const fitsAbove = anchor.top - gap - VIEWPORT_MARGIN >= bubbleHeight;
  if (fitsBelow || fitsAbove) return vertical();

  const clampedTop = Math.min(
    Math.max(VIEWPORT_MARGIN, anchor.top),
    Math.max(VIEWPORT_MARGIN, viewportHeight - bubbleHeight - VIEWPORT_MARGIN)
  );
  if (viewportWidth - anchor.right - gap - VIEWPORT_MARGIN >= bubbleWidth) {
    return { top: clampedTop, left: anchor.right + gap };
  }
  if (anchor.left - gap - VIEWPORT_MARGIN >= bubbleWidth) {
    return { top: clampedTop, left: anchor.left - gap - bubbleWidth };
  }
  return vertical();
};

/**
 * Sets `inert` on every direct child of `body` except `keep`, including children added later, and
 * returns a function that restores exactly what it changed. An element that was already inert stays
 * inert afterwards.
 */
export const inertSiblings = (keep: Element): (() => void) => {
  const changed = new Set<Element>();
  const apply = (element: Element) => {
    if (element === keep || element.hasAttribute("inert")) return;
    element.setAttribute("inert", "");
    // Says who inerted it: see TOUR_INERT_ATTRIBUTE. Removed again with the inert it belongs to.
    element.setAttribute(TOUR_INERT_ATTRIBUTE, "");
    changed.add(element);
  };

  Array.from(document.body.children).forEach(apply);

  const observer =
    typeof MutationObserver === "undefined"
      ? null
      : new MutationObserver((records) => {
          records.forEach((record) =>
            record.addedNodes.forEach((node) => {
              if (node instanceof Element && node.parentElement === document.body) apply(node);
            })
          );
        });
  observer?.observe(document.body, { childList: true });

  return () => {
    observer?.disconnect();
    changed.forEach((element) => {
      element.removeAttribute("inert");
      element.removeAttribute(TOUR_INERT_ATTRIBUTE);
    });
    changed.clear();
  };
};

export const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
};

/** Next animation frame, with a timer fallback for environments that have no rAF. */
export const scheduleFrame = (callback: () => void): (() => void) => {
  if (typeof requestAnimationFrame === "function") {
    const frame = requestAnimationFrame(callback);
    return () => cancelAnimationFrame(frame);
  }
  const timer = setTimeout(callback, 16);
  return () => clearTimeout(timer);
};
